import { EmployeeSelectionService } from './EmployeeSelectionService';

// Cache entry structure
interface AvailabilityCacheEntry {
  data: Record<string, { isAvailable: boolean; conflicts: any[] }>;
  timestamp: number;
  employeeIds: string[];
  startTime: number;
  endTime: number;
  excludeShiftId?: string;
}

// Cache configuration
const CACHE_TTL = 30 * 1000; // 30 seconds
const MAX_CACHE_ENTRIES = 50; // Prevent memory bloat

/**
 * Smart availability cache that:
 * 1. Caches availability results for instant display
 * 2. Invalidates cache when shifts/assignments change
 * 3. Provides background refresh capabilities
 */
class EmployeeAvailabilityCacheService {
  private cache = new Map<string, AvailabilityCacheEntry>();
  private pendingRequests = new Map<string, Promise<any>>();
  private listeners = new Set<() => void>();

  /**
   * Generate cache key from availability check parameters
   */
  private getCacheKey(
    employeeIds: string[],
    startTime: Date,
    endTime: Date,
    excludeShiftId?: string
  ): string {
    const sortedIds = [...employeeIds].sort().join(',');
    const timeRange = `${startTime.getTime()}-${endTime.getTime()}`;
    const exclude = excludeShiftId || 'none';
    return `${sortedIds}|${timeRange}|${exclude}`;
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(entry: AvailabilityCacheEntry): boolean {
    return Date.now() - entry.timestamp < CACHE_TTL;
  }

  /**
   * Check if cache entry is near expiry (for background refresh)
   */
  private isCacheNearExpiry(entry: AvailabilityCacheEntry): boolean {
    return Date.now() - entry.timestamp > CACHE_TTL * 0.8; // 80% of TTL
  }

  /**
   * Clean up old cache entries to prevent memory bloat
   */
  private cleanupCache(): void {
    if (this.cache.size <= MAX_CACHE_ENTRIES) return;

    // Remove oldest entries
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toRemove = entries.slice(0, entries.length - MAX_CACHE_ENTRIES);
    toRemove.forEach(([key]) => this.cache.delete(key));

  }

  /**
   * Get availability data with smart caching
   * Returns cached data instantly if available, optionally refreshes in background
   */
  async getAvailability(
    employeeIds: string[],
    startTime: Date,
    endTime: Date,
    excludeShiftId?: string,
    backgroundRefresh: boolean = false
  ): Promise<{
    data: Record<string, { isAvailable: boolean; conflicts: any[] }>;
    fromCache: boolean;
    refreshing: boolean;
  }> {
    const cacheKey = this.getCacheKey(employeeIds, startTime, endTime, excludeShiftId);
    const cachedEntry = this.cache.get(cacheKey);


    // Return cached data if valid
    if (cachedEntry && this.isCacheValid(cachedEntry)) {
      
      // Background refresh if near expiry
      if (backgroundRefresh && this.isCacheNearExpiry(cachedEntry)) {
        this.fetchAndCache(employeeIds, startTime, endTime, excludeShiftId, cacheKey);
        return { data: cachedEntry.data, fromCache: true, refreshing: true };
      }

      return { data: cachedEntry.data, fromCache: true, refreshing: false };
    }

    // Fetch fresh data
    const data = await this.fetchAndCache(employeeIds, startTime, endTime, excludeShiftId, cacheKey);
    return { data, fromCache: false, refreshing: false };
  }

  /**
   * Fetch availability data and update cache
   */
  private async fetchAndCache(
    employeeIds: string[],
    startTime: Date,
    endTime: Date,
    excludeShiftId?: string,
    cacheKey?: string
  ): Promise<Record<string, { isAvailable: boolean; conflicts: any[] }>> {
    const key = cacheKey || this.getCacheKey(employeeIds, startTime, endTime, excludeShiftId);
    
    // Prevent duplicate requests
    if (this.pendingRequests.has(key)) {
      return await this.pendingRequests.get(key)!;
    }

    // Create and cache the promise
    const promise = EmployeeSelectionService.checkMultipleEmployeeAvailability(
      employeeIds,
      startTime,
      endTime,
      excludeShiftId
    );
    
    this.pendingRequests.set(key, promise);

    try {
      const data = await promise;
      
      // Cache the result
      const entry: AvailabilityCacheEntry = {
        data,
        timestamp: Date.now(),
        employeeIds: [...employeeIds],
        startTime: startTime.getTime(),
        endTime: endTime.getTime(),
        excludeShiftId
      };
      
      this.cache.set(key, entry);
      
      // Cleanup old entries
      this.cleanupCache();
      
      // Notify listeners of cache update
      this.notifyListeners();
      
      return data;
    } finally {
      // Clean up pending request
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Invalidate cache entries that might be affected by shift/assignment changes
   * Called whenever shifts are created, updated, deleted, or assignments change
   */
  invalidateCache(reason: string, affectedEmployeeIds?: string[]): void {
    const sizeBefore = this.cache.size;
    
    if (affectedEmployeeIds && affectedEmployeeIds.length > 0) {
      // Targeted invalidation - remove entries that include affected employees
      const keysToDelete: string[] = [];
      
      for (const [key, entry] of this.cache.entries()) {
        const hasAffectedEmployee = affectedEmployeeIds.some(id => 
          entry.employeeIds.includes(id)
        );
        if (hasAffectedEmployee) {
          keysToDelete.push(key);
        }
      }
      
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      // Full cache invalidation
      this.cache.clear();
    }
    
    const sizeAfter = this.cache.size;
    
    if (sizeBefore !== sizeAfter) {
      // Notify listeners of cache changes
      this.notifyListeners();
    }
  }

  /**
   * Pre-load availability data for faster access
   * Useful for hover effects or anticipated user actions
   */
  async preload(
    employeeIds: string[],
    startTime: Date,
    endTime: Date,
    excludeShiftId?: string
  ): Promise<void> {
    const cacheKey = this.getCacheKey(employeeIds, startTime, endTime, excludeShiftId);
    const cachedEntry = this.cache.get(cacheKey);

    // Only preload if cache is invalid or near expiry
    if (!cachedEntry || !this.isCacheValid(cachedEntry) || this.isCacheNearExpiry(cachedEntry)) {
      await this.fetchAndCache(employeeIds, startTime, endTime, excludeShiftId, cacheKey);
    }
  }

  /**
   * Subscribe to cache changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of cache changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch {
        // Silently ignore listener errors to prevent cascading failures
      }
    });
  }

  /**
   * Get cache statistics for debugging
   */
  getStats(): {
    cacheSize: number;
    pendingRequests: number;
    listeners: number;
  } {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      listeners: this.listeners.size
    };
  }

  /**
   * Clear all cache and pending requests (for testing)
   */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    this.notifyListeners();
  }
}

// Export singleton instance
export const employeeAvailabilityCache = new EmployeeAvailabilityCacheService();
export default employeeAvailabilityCache;